import React, { useState } from 'react';
import { styled } from 'styled-components';
const FormatJSON = (props) => {
    const [copied, setCopied] = useState(false);
    const { json, compact = false, collapsed = false } = props;
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(json, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        }
        catch (e) {
            setCopied(false);
        }
    };
    return (React.createElement(PreContainer, { compact: compact },
        React.createElement(CopyButton, { compact: compact, onClick: handleCopy, title: "Copy JSON" }, copied ? '✓ Copied!' : '⧉ Copy'),
        React.createElement(StyledPre, { compact: compact },
            React.createElement(JSONNode, { value: json, compact: compact, collapsed: collapsed, level: 0 }))));
};
// Arrow: subtle, rounded, slightly larger, with hover highlight
const Arrow = styled.span `
  display: inline-block;
  width: ${(props) => (props.compact ? '1em' : '1.2em')};
  cursor: pointer;
  user-select: none;
  transition:
    transform 0.15s,
    background 0.15s;
  color: #b3b3b3;
  background: ${(props) => (props.open ? '#393939' : 'transparent')};
  border-radius: 50%;
  transform: rotate(${(props) => (props.open ? '90deg' : '0deg')});
  margin-right: 0.2em;
  position: relative;
  left: 0;
  font-size: ${(props) => (props.compact ? '1em' : '1.1em')};
  height: 1.2em;
  text-align: center;
  line-height: 1.2em;
  vertical-align: middle;
  /* Indent the arrow by nesting level */
  margin-left: ${(props) => props.compact
    ? `calc(${props.level} * 1em - ${props.compact ? '1.6em' : '1.8em'})`
    : `calc(${props.level} * 2em - ${props.compact ? '1.6em' : '1.8em'})`};
  &:hover {
    background: #444;
    color: #fff;
  }
`;
const KeySpan = styled.span `
  color: #ff6f91;
  cursor: pointer;
  user-select: none;
  font-size: ${(props) => (props.compact ? '0.97em' : '1.03em')};
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: color 0.15s;
  &:hover {
    color: #ffb3c6;
  }
`;
const StringSpan = styled.span `
  color: #ffe066;
  font-size: ${(props) => (props.compact ? '0.97em' : '1.03em')};
  font-style: italic;
  transition: color 0.15s;
`;
const NumberSpan = styled.span `
  color: #a3c9f7;
  font-size: ${(props) => (props.compact ? '0.97em' : '1.03em')};
  font-weight: 500;
  transition: color 0.15s;
`;
const BooleanSpan = styled.span `
  color: #6ee7b7;
  font-size: ${(props) => (props.compact ? '0.97em' : '1.03em')};
  font-weight: 500;
  transition: color 0.15s;
`;
const NullSpan = styled.span `
  color: #bdbdbd;
  font-size: ${(props) => (props.compact ? '0.97em' : '1.03em')};
  font-style: italic;
  opacity: 0.7;
  transition: color 0.15s;
`;
const Indent = styled.span `
  display: inline-block;
  width: ${(props) => (props.compact ? '1em' : '2em')};
`;
const Row = styled.div `
  position: relative;
  ${(props) => props.compact
    ? `
    font-size: 0.97em;
    margin-bottom: 0.08em;
    line-height: 1.25;
  `
    : `
    margin-bottom: 0.13em;
    line-height: 1.35;
  `}
  &:hover {
    background: #282828;
  }
`;
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isArray(value) {
    return Array.isArray(value);
}
const JSONNode = ({ value, name, level = 0, compact = false, collapsed = false, }) => {
    // If collapsed is true, all nodes except root (level 0) start collapsed
    const isRoot = level === 0;
    const [open, setOpen] = useState(collapsed ? isRoot : compact ? level < 1 : level < 3);
    if (isObject(value)) {
        const keys = Object.keys(value);
        if (name !== undefined) {
            return (React.createElement(Row, { compact: compact },
                React.createElement(Arrow, { open: open, compact: compact, level: level, onClick: () => setOpen((o) => !o), title: open ? 'Collapse' : 'Expand' }, "\u25B6"),
                React.createElement(KeySpan, { compact: compact, onClick: () => setOpen((o) => !o) },
                    "\"",
                    name,
                    "\""),
                React.createElement("span", null,
                    ": ",
                    React.createElement("span", { style: { color: '#888' } }, '{')),
                open && keys.length > 0 ? (React.createElement("div", null,
                    keys.map((k) => (React.createElement(JSONNode, { key: k, value: value[k], name: k, level: level + 1, compact: compact, collapsed: collapsed }))),
                    React.createElement(Indent, { compact: compact, style: {
                            width: compact ? `${level * 1}em` : `${level * 2}em`,
                        } }),
                    React.createElement("span", { style: { color: '#888' } }, '}'))) : (!open && React.createElement("span", { style: { color: '#888' } },
                    "\u2026",
                    ' }'))));
        }
        else {
            // root object, no key
            return (React.createElement(Row, { compact: compact },
                React.createElement("span", { style: { color: '#888' } }, '{'),
                open && keys.length > 0 ? (React.createElement("div", null,
                    keys.map((k) => (React.createElement(JSONNode, { key: k, value: value[k], name: k, level: level + 1, compact: compact, collapsed: collapsed }))),
                    React.createElement(Indent, { compact: compact, style: {
                            width: compact ? `${level * 1}em` : `${level * 2}em`,
                        } }),
                    React.createElement("span", { style: { color: '#888' } }, '}'))) : (!open && React.createElement("span", { style: { color: '#888' } },
                    "\u2026",
                    ' }'))));
        }
    }
    else if (isArray(value)) {
        if (name !== undefined) {
            return (React.createElement(Row, { compact: compact },
                React.createElement(Arrow, { open: open, compact: compact, level: level, onClick: () => setOpen((o) => !o), title: open ? 'Collapse' : 'Expand' }, "\u25B6"),
                React.createElement(KeySpan, { compact: compact, onClick: () => setOpen((o) => !o) },
                    "\"",
                    name,
                    "\""),
                React.createElement("span", null,
                    ": ",
                    React.createElement("span", { style: { color: '#888' } }, "[")),
                value.length === 0 ? (React.createElement("span", null,
                    ' ',
                    React.createElement("span", { style: { fontStyle: 'italic', color: '#888' } }, "empty"),
                    ' ',
                    React.createElement("span", { style: { color: '#888' } }, "]"))) : open ? (React.createElement("div", null,
                    value.map((v, idx) => isObject(v) ? (React.createElement(Row, { key: idx, compact: compact },
                        React.createElement(Indent, { compact: compact, style: {
                                width: compact
                                    ? `${(level + 1) * 1}em`
                                    : `${(level + 1) * 2}em`,
                            } }),
                        React.createElement("span", { style: { color: '#888' } }, '{'),
                        React.createElement("div", null,
                            Object.keys(v).map((k) => (React.createElement(JSONNode, { key: k, value: v[k], name: k, level: level + 2, compact: compact, collapsed: collapsed }))),
                            React.createElement(Indent, { compact: compact, style: {
                                    width: compact
                                        ? `${(level + 1) * 1}em`
                                        : `${(level + 1) * 2}em`,
                                } }),
                            React.createElement("span", { style: { color: '#888' } }, '}')))) : isArray(v) ? (React.createElement(Row, { key: idx, compact: compact },
                        React.createElement(Indent, { compact: compact, style: {
                                width: compact
                                    ? `${(level + 1) * 1}em`
                                    : `${(level + 1) * 2}em`,
                            } }),
                        React.createElement("span", { style: { color: '#888' } }, '['),
                        React.createElement("div", null,
                            v.map((item, i) => (React.createElement(JSONNode, { key: i, value: item, level: level + 2, compact: compact, collapsed: collapsed }))),
                            React.createElement(Indent, { compact: compact, style: {
                                    width: compact
                                        ? `${(level + 1) * 1}em`
                                        : `${(level + 1) * 2}em`,
                                } }),
                            React.createElement("span", { style: { color: '#888' } }, ']')))) : (React.createElement(JSONNode, { key: idx, value: v, level: level + 1, compact: compact, collapsed: collapsed }))),
                    React.createElement(Indent, { compact: compact, style: {
                            width: compact ? `${level * 1}em` : `${level * 2}em`,
                        } }),
                    React.createElement("span", { style: { color: '#888' } }, ']'))) : (!open && (React.createElement("span", null,
                    React.createElement("span", { style: { color: '#888' } }, "\u2026]"),
                    ' ',
                    React.createElement("span", { style: { color: '#888', fontStyle: 'italic' } },
                        "(",
                        value.length,
                        " ",
                        value.length === 1 ? 'element' : 'elements',
                        ")"))))));
        }
        else {
            // root array, no key
            return (React.createElement(Row, { compact: compact },
                React.createElement("span", { style: { color: '#888' } }, '['),
                value.length === 0 ? (React.createElement("span", { style: { color: '#888' } },
                    React.createElement("span", { style: { fontStyle: 'italic', color: '#888' } }, "empty"),
                    React.createElement("span", { style: { color: '#888' } }, "]"))) : open ? (React.createElement("div", null,
                    value.map((v, idx) => isObject(v) ? (React.createElement(Row, { key: idx, compact: compact },
                        React.createElement(Indent, { compact: compact, style: {
                                width: compact
                                    ? `${(level + 1) * 1}em`
                                    : `${(level + 1) * 2}em`,
                            } }),
                        React.createElement("span", { style: { color: '#888' } }, '{'),
                        React.createElement("div", null,
                            Object.keys(v).map((k) => (React.createElement(JSONNode, { key: k, value: v[k], name: k, level: level + 2, compact: compact, collapsed: collapsed }))),
                            React.createElement(Indent, { compact: compact, style: {
                                    width: compact
                                        ? `${(level + 1) * 1}em`
                                        : `${(level + 1) * 2}em`,
                                } }),
                            React.createElement("span", { style: { color: '#888' } }, '}')))) : isArray(v) ? (React.createElement(Row, { key: idx, compact: compact },
                        React.createElement(Indent, { compact: compact, style: {
                                width: compact
                                    ? `${(level + 1) * 1}em`
                                    : `${(level + 1) * 2}em`,
                            } }),
                        React.createElement("span", { style: { color: '#888' } }, '['),
                        React.createElement("div", null,
                            v.map((item, i) => (React.createElement(JSONNode, { key: i, value: item, level: level + 2, compact: compact, collapsed: collapsed }))),
                            React.createElement(Indent, { compact: compact, style: {
                                    width: compact
                                        ? `${(level + 1) * 1}em`
                                        : `${(level + 1) * 2}em`,
                                } }),
                            React.createElement("span", { style: { color: '#888' } }, ']')))) : (React.createElement(JSONNode, { key: idx, value: v, level: level + 1, compact: compact, collapsed: collapsed }))),
                    React.createElement(Indent, { compact: compact, style: {
                            width: compact ? `${level * 1}em` : `${level * 2}em`,
                        } }),
                    React.createElement("span", { style: { color: '#888' } }, ']'))) : (!open && (React.createElement("span", null,
                    React.createElement("span", { style: { color: '#888' } }, "\u2026]"),
                    ' ',
                    React.createElement("span", { style: { color: '#888', fontStyle: 'italic' } },
                        "(",
                        value.length,
                        " ",
                        value.length === 1 ? 'element' : 'elements',
                        ")"))))));
        }
    }
    else {
        let ValueSpan = StringSpan;
        let displayValue = value;
        if (typeof value === 'string') {
            displayValue = `"${value}"`;
            ValueSpan = StringSpan;
        }
        else if (typeof value === 'number') {
            ValueSpan = NumberSpan;
        }
        else if (typeof value === 'boolean') {
            ValueSpan = BooleanSpan;
            displayValue = value ? 'true' : 'false';
        }
        else if (value === null) {
            ValueSpan = NullSpan;
            displayValue = 'null';
        }
        return (React.createElement(Row, { compact: compact },
            name !== undefined && (React.createElement(React.Fragment, null,
                React.createElement(Indent, { compact: compact, style: {
                        width: compact ? `${level * 1}em` : `${level * 2}em`,
                    } }),
                React.createElement(KeySpan, { compact: compact },
                    "\"",
                    name,
                    "\""),
                React.createElement("span", null, ": "))),
            React.createElement(ValueSpan, { compact: compact }, displayValue)));
    }
};
const PreContainer = styled.div `
  position: relative;
  display: inline-block;
  width: 100%;
  ${(props) => props.compact
    ? `
    font-size: 0.97em;
    padding: 0.25em 0.25em 0.25em 0.25em;
    min-width: 0;
  `
    : `
    padding: 0.5em 0.5em 0.5em 0.5em;
  `}
  background: linear-gradient(90deg, #232526 0%, #2d2d2d 100%);
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(30, 30, 30, 0.08);
`;
const CopyButton = styled.button `
  position: absolute;
  top: ${(props) => (props.compact ? '0.3em' : '0.7em')};
  right: ${(props) => (props.compact ? '0.5em' : '1em')};
  background: #393939;
  color: #ffe066;
  border: none;
  border-radius: 5px;
  padding: ${(props) => (props.compact ? '0.15em 0.7em' : '0.3em 1em')};
  font-size: ${(props) => (props.compact ? '0.85em' : '1em')};
  cursor: pointer;
  z-index: 2;
  opacity: 0.92;
  box-shadow: 0 1px 4px 0 rgba(30, 30, 30, 0.07);
  transition:
    background 0.15s,
    opacity 0.15s,
    color 0.15s;
  &:hover {
    background: #444;
    color: #fff;
    opacity: 1;
  }
`;
const StyledPre = styled.pre `
  background: transparent;
  color: #eaeaea;
  padding: ${(props) => (props.compact ? '0.7em' : '1.2em')};
  border-radius: 8px;
  overflow: auto;
  font-family: 'Fira Mono', 'Consolas', 'Menlo', monospace;
  font-size: ${(props) => (props.compact ? '0.97em' : '1.05em')};
  margin: 0;
  min-width: 0;
  border: none;

  .key {
    color: #ff6f91;
  }
  .string {
    color: #ffe066;
  }
  .number {
    color: #a3c9f7;
  }
  .boolean {
    color: #6ee7b7;
  }
  .null {
    color: #bdbdbd;
  }
`;
export default FormatJSON;
//# sourceMappingURL=FormatJSON.js.map